/**
 * RedesignBanner
 *
 * Says out loud that the product is being redressed, so a half-converted app
 * reads as work in progress rather than as something broken. It sits with the
 * other account-wide bars under the header.
 *
 * Dismissal is permanent (localStorage), not per-session: this is one piece
 * of news, and a banner that comes back every morning stops being news and
 * starts being furniture. The key carries a number so a later announcement
 * can be made without un-dismissing this one for everybody.
 */

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import { Button } from '@/design-system/primitives'
import { TopBanner } from '@/design-system/feedback'

const DISMISS_KEY = 'redesign-banner-dismissed:1'

/** Exported so the test can read the copy without rendering the whole shell. */
export const REDESIGN_BANNER_COPY = {
  lead: 'Pardon our pixel dust.',
  rest: "We're making SuperSchema look even more super, one page at a time.",
}

export default function RedesignBanner() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      // Storage can be unavailable (private mode, blocked cookies). The
      // banner showing is the safe failure; it is not worth an error.
      return false
    }
  })

  useEffect(() => {
    if (!dismissed) return
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Dismissal still holds for this page view.
    }
  }, [dismissed])

  if (dismissed) return null

  /*
    The sidebar is `fixed` from the header down and the banners are not
    inside <main>, which is the element that clears it with the rail width. So
    every bar in this slot has its left 12rem underneath the sidebar. This
    one starts where the page content starts.
  */
  return (
    <DsIsland panel={false} ground={false} className="md:ml-[var(--app-rail)]">
      {/* DsIsland takes no arbitrary props, so the hook the test and any
          future analytics need sits on its own element inside. */}
      <div data-testid="redesign-banner">
      <TopBanner
        kind="loud"
        action={
          <Button
            variant="ghost"
            size="sm"
            icon
            onClick={() => setDismissed(true)}
            aria-label="Dismiss this message"
          >
            <X aria-hidden="true" />
          </Button>
        }
      >
        <b>{REDESIGN_BANNER_COPY.lead}</b> {REDESIGN_BANNER_COPY.rest}
      </TopBanner>
      </div>
    </DsIsland>
  )
}
